import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtErr } from "../api";
import { ConsultBadge, CONSULTATION_STATUSES } from "./Consultations";
import { ArrowLeft, Send, Phone, Mail } from "lucide-react";

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</p>
    <p className="mt-1 text-[15px] text-brand-navy font-medium whitespace-pre-wrap">{value || "—"}</p>
  </div>
);

export default function ConsultationDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/admin/consultations/${id}`).then((res) => {
    setC(res.data);
    setStatus(res.data.status);
  }).catch(() => setC(false));

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (c === null) return <p className="text-slate-500 font-semibold">Loading request…</p>;
  if (c === false) return <p className="text-red-700 font-semibold">Consultation request not found.</p>;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/consultations/${id}`, { status });
      toast.success("Status updated.");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      await api.post(`/admin/consultations/${id}/notes`, { text: note });
      setNote("");
      toast.success("Internal note added.");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div data-testid="consultation-detail">
      <Link to="/portal/consultations" data-testid="back-to-consultations" className="inline-flex items-center gap-2 text-sm font-bold text-brand-teal hover:text-brand-navy">
        <ArrowLeft className="h-4 w-4" /> Back to Consultations
      </Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-brand-navy">{c.name}</h1>
          <p className="mt-1 text-sm text-slate-500">Submitted {new Date(c.submitted_at).toLocaleString()}</p>
        </div>
        <ConsultBadge value={c.status} />
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
        <h2 className="font-display text-xl text-brand-navy">Request Details</h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Phone</p>
            <a href={`tel:${c.phone.replace(/[^+\d]/g, "")}`} data-testid="consultation-phone" className="mt-1 inline-flex items-center gap-1.5 text-[15px] font-semibold text-brand-teal hover:text-brand-navy">
              <Phone className="h-4 w-4" /> {c.phone}
            </a>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Email</p>
            <a href={`mailto:${c.email}`} data-testid="consultation-email" className="mt-1 inline-flex items-center gap-1.5 text-[15px] font-semibold text-brand-teal hover:text-brand-navy break-all">
              <Mail className="h-4 w-4" /> {c.email}
            </a>
          </div>
          <Info label="Care Needed For" value={c.care_for} />
          <Info label="Preferred Date" value={c.preferred_date} />
          <Info label="Preferred Time" value={c.preferred_time} />
          <div className="sm:col-span-2"><Info label="Message" value={c.message} /></div>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
        <h2 className="font-display text-xl text-brand-navy">Status</h2>
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="con-status" className="block text-sm font-bold text-brand-navy">Request Status</label>
            <select id="con-status" data-testid="consultation-status-select" value={status} onChange={(e) => setStatus(e.target.value)} className="mt-2 rounded-xl border border-slate-300 px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60">
              {CONSULTATION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <button onClick={save} disabled={saving} data-testid="consultation-save-status" className="rounded-full bg-brand-teal px-6 py-3 text-[15px] font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95 disabled:opacity-60">
            {saving ? "Saving…" : "Save Status"}
          </button>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8">
        <h2 className="font-display text-xl text-brand-navy">Internal Notes</h2>
        <p className="mt-1 text-xs text-slate-500">Private — visible only to authorized office staff.</p>
        <ul className="mt-5 space-y-3">
          {(c.internal_notes || []).length === 0 && <li className="text-sm text-slate-500">No notes yet.</li>}
          {(c.internal_notes || []).map((n) => (
            <li key={n.id} className="rounded-xl bg-brand-mist/60 px-4 py-3">
              <p className="text-[15px] text-brand-navy">{n.text}</p>
              <p className="mt-1 text-xs text-slate-500">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
            </li>
          ))}
        </ul>
        <form onSubmit={addNote} className="mt-5 flex gap-3">
          <input
            data-testid="consultation-note-input"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Called and confirmed Thursday morning…"
            className="flex-1 rounded-xl border border-slate-300 px-4 py-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-teal/60"
          />
          <button type="submit" data-testid="consultation-note-add" className="inline-flex items-center gap-2 rounded-full bg-brand-teal px-5 py-3 text-sm font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95">
            <Send className="h-4 w-4" /> Add Note
          </button>
        </form>
      </div>
    </div>
  );
}
